import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Maximize2, X } from 'lucide-react';
import { Slider } from './ui/slider';
import { Input } from './ui/input';
import { Button } from './ui/button';

const SearchFilters = ({ onFilterChange, showMap, onToggleView }) => {
  const [location, setLocation] = useState('');
  const [radius, setRadius] = useState([0]);
  const [maxBudget, setMaxBudget] = useState('');
  const [minSurface, setMinSurface] = useState('');
  const [maxSurface, setMaxSurface] = useState('');

  const applyFilters = useCallback(() => {
    onFilterChange({
      location,
      radius: radius[0],
      maxBudget: maxBudget ? parseInt(maxBudget) : null,
      minSurface: minSurface ? parseInt(minSurface) : null,
      maxSurface: maxSurface ? parseInt(maxSurface) : null
    });
  }, [location, radius, maxBudget, minSurface, maxSurface, onFilterChange]);

  // Auto-apply filters with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      applyFilters();
    }, 300);
    return () => clearTimeout(timer);
  }, [applyFilters]);

  const handleValidate = () => {
    applyFilters();
  };

  const handleReset = () => {
    setLocation('');
    setRadius([0]);
    setMaxBudget('');
    setMinSurface('');
    setMaxSurface('');
  };

  const hasActiveFilters = location || radius[0] > 0 || maxBudget || minSurface || maxSurface;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-semibold text-gray-800">Filtres de recherche</h3>
        {hasActiveFilters && (
          <button
            onClick={handleReset}
            className="text-xs text-[#c4d600] hover:text-[#b0c200] flex items-center transition-colors"
            type="button"
          >
            <X className="w-3 h-3 mr-1" />
            Réinitialiser
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-4">Ville, CP, département ou région</p>
      
      {/* Location Input */}
      <div className="mb-6">
        <div className="relative">
          <Input
            type="text"
            placeholder="Localisation"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="w-full pl-4 pr-10 bg-[#c4d600] border-none text-white placeholder-white placeholder-opacity-90"
          />
          <MapPin className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white" />
        </div>
      </div>

      {/* Radius Slider */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm text-gray-600">Dans un rayon de</label>
          <span className="text-sm font-semibold text-gray-800">{radius[0]} km</span>
        </div>
        <Slider
          value={radius}
          onValueChange={setRadius}
          max={50}
          step={1}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0 km</span>
          <span>50 km</span>
        </div>
      </div>

      {/* Budget */}
      <div className="mb-6">
        <label className="text-sm text-gray-600 block mb-2">Budget max</label>
        <div className="relative">
          <Input
            type="text"
            placeholder="Montant"
            value={maxBudget}
            onChange={(e) => setMaxBudget(e.target.value.replace(/\D/g, ''))}
            className="w-full pl-4 pr-8 bg-white border-gray-300"
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">€</span>
        </div>
      </div>

      {/* Surface */}
      <div className="mb-6">
        <label className="text-sm text-gray-600 block mb-2">Surface</label>
        <div className="grid grid-cols-2 gap-3">
          <div className="relative">
            <Input
              type="text"
              placeholder="Surface Min."
              value={minSurface}
              onChange={(e) => setMinSurface(e.target.value.replace(/\D/g, ''))}
              className="w-full pl-4 pr-8 bg-white border-gray-300"
            />
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">m²</span>
          </div>
          <div className="relative">
            <Input
              type="text"
              placeholder="Surface Max."
              value={maxSurface}
              onChange={(e) => setMaxSurface(e.target.value.replace(/\D/g, ''))}
              className="w-full pl-4 pr-8 bg-white border-gray-300"
            />
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">m²</span>
          </div>
        </div>
      </div>

      {/* Validate Button */}
      <Button
        onClick={handleValidate}
        className="w-full bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
      >
        Appliquer les filtres
      </Button>
    </div>
  );
};

export default SearchFilters;